import React, { useState, useEffect } from 'react';
import {
  Alert,
  Keyboard,
  Platform,
  StatusBar,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  KeyboardAvoidingView,
} from 'react-native';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import LinearGradient from 'react-native-linear-gradient';

import { PyxiLabel } from '~assets/icons';
import ModernButton from '~components/ModernButton';
import ModernTextInput from '~components/ModernTextInput';
import ModernCard from '~components/ModernCard/ModernCard';
import { FadeIn } from '~components/MicroInteractions';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, borderRadius, typography, shadows } from '~constants/design';
import { SCREENS } from '~constants';
import { NavigationProps } from '~types/navigation/navigation.type';
import { logScreenView } from '~Utils/firebaseAnalytics';
import { LanguagesModal } from '~components/ModalWithItems/LanguagesModal';
import { useLanguage } from '~containers/Core/navigation/SettingsStack/zustand';
import { useResetPassword } from '~hooks/auth/useResetPassword';

interface RouteParams {
  email: string;
}

const ResetPassword: React.FC = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { height } = useWindowDimensions();
  const navigation = useNavigation<NavigationProps>();
  const route = useRoute();
  const { email } = (route.params || {}) as RouteParams;
  const { bottom } = useSafeAreaInsets();
  const isFocused = useIsFocused();
  const { language } = useLanguage();
  const isAndroid = Platform.OS === 'android';

  const [isLoading, setIsLoading] = useState(false);
  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false);

  const { mutateAsync: resetPasswordApi } = useResetPassword();

  useEffect(() => {
    if (isFocused) {
      logScreenView('ResetPassword', 'ResetPassword');
    }
  }, [isFocused]);

  const paddingBottom = isAndroid ? bottom + spacing.lg : bottom;

  const validationSchema = yup.object().shape({
    otp: yup
      .string()
      .min(4, t('auth.otp_min_length') || 'OTP must be at least 4 digits')
      .max(8, t('auth.otp_max_length') || 'OTP cannot exceed 8 digits')
      .matches(/^[0-9]+$/, t('auth.otp_numeric') || 'OTP must contain only numbers')
      .required(t('auth.otp_required') || 'OTP is required'),
    password: yup
      .string()
      .min(8, t('auth.password_min_length') || 'Password must be at least 8 characters')
      .matches(/[A-Z]/, t('auth.password_uppercase') || 'Password must contain at least one uppercase letter')
      .matches(/[a-z]/, t('auth.password_lowercase') || 'Password must contain at least one lowercase letter')
      .matches(/[0-9]/, t('auth.password_number') || 'Password must contain at least one number')
      .test('password-strength', t('auth.password_weak') || 'Password is too weak', function(value) {
        if (!value) return false;
        const strength = calculatePasswordStrength(value);
        return strength >= 2; // Require at least medium strength
      })
      .required(t('auth.password_required') || 'Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('auth.passwords_must_match') || 'Passwords must match')
      .required(t('auth.confirm_password_required') || 'Please confirm your password'),
  });

  // Modern styles matching Login screen
  const styles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContainer: {
      flexGrow: 1,
      minHeight: height - (StatusBar.currentHeight || 0),
      paddingBottom: spacing.xl,
    },
    heroSection: {
      height: height * 0.28,
      position: 'relative' as const,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      overflow: 'hidden' as const,
    },
    heroGradient: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    heroContent: {
      flex: 1,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      zIndex: 2,
      paddingHorizontal: spacing.lg,
    },
    decorativeCircle1: {
      position: 'absolute' as const,
      top: -50,
      right: -50,
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: colors.textInverse + '15',
    },
    decorativeCircle2: {
      position: 'absolute' as const,
      bottom: -30,
      left: -30,
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.textInverse + '10',
    },
    decorativeCircle3: {
      position: 'absolute' as const,
      top: 120,
      left: -20,
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: colors.textInverse + '20',
    },
    languageButton: {
      position: 'absolute' as const,
      top: spacing.md,
      right: spacing.md,
      backgroundColor: colors.textInverse + '10',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.full,
      borderWidth: 1,
      borderColor: colors.textInverse + '20',
      zIndex: 3,
      minWidth: 44,
      height: 32,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    languageText: {
      color: colors.textInverse,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      letterSpacing: 0.3,
      opacity: 0.9,
    },
    logoContainer: {
      alignItems: 'center' as const,
      marginBottom: spacing.lg,
      transform: [{ scale: 1.1 }],
    },
    taglineText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.infoDark,
      textAlign: 'center' as const,
      marginTop: spacing.md,
      opacity: 0.95,
      letterSpacing: 0.5,
    },
    formSection: {
      paddingHorizontal: 0,
      paddingTop: spacing.lg,
    },
    formTitle: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center' as const,
      marginBottom: spacing.sm,
      letterSpacing: -0.3,
    },
    formSubtitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      textAlign: 'center' as const,
      marginBottom: spacing.lg,
      opacity: 0.85,
      letterSpacing: 0.1,
      lineHeight: typography.fontSize.base * 1.4,
    },
    inputContainer: {
      marginBottom: spacing.sm,
    },
    passwordStrength: {
      marginTop: spacing.xs,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
    },
    strengthBar: {
      flex: 1,
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      marginRight: spacing.sm,
      overflow: 'hidden' as const,
    },
    strengthFill: {
      height: 4,
      borderRadius: 2,
    },
    strengthText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
    },
    bottomSection: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.lg,
      paddingBottom: Math.max(paddingBottom, spacing.lg),
      alignItems: 'center' as const,
      marginTop: spacing.md,
    },
    backToLoginContainer: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      marginTop: spacing.md,
    },
    backToLoginText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      fontWeight: typography.fontWeight.normal,
    },
    backToLoginButton: {
      marginLeft: spacing.xs,
    },
    backToLoginButtonText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.primary,
    },
  };

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const getPasswordStrengthColor = (strength: number) => {
    switch (strength) {
      case 0:
      case 1:
        return colors.error;
      case 2:
      case 3:
        return colors.warning;
      case 4:
      case 5:
        return colors.success;
      default:
        return colors.border;
    }
  };

  const getPasswordStrengthText = (strength: number) => {
    switch (strength) {
      case 0:
      case 1:
        return t('auth.password_strength_weak') || 'Weak';
      case 2:
      case 3:
        return t('auth.password_strength_medium') || 'Medium';
      case 4:
      case 5:
        return t('auth.password_strength_strong') || 'Strong';
      default:
        return '';
    }
  };

  const handleSubmit = async (values: any, { setErrors, setTouched }: any) => {
    // First validate the form manually
    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (validationError: any) {
      // Mark all fields as touched to show errors
      setTouched({
        otp: true,
        password: true,
        confirmPassword: true,
      });

      // Set field errors
      if (validationError.inner) {
        const errors: any = {};
        validationError.inner.forEach((error: any) => {
          errors[error.path] = error.message;
        });
        setErrors(errors);
      }

      // Show alert with validation errors
      const errorMessages = validationError.inner?.map((error: any) => error.message) || [validationError.message];
      Alert.alert(
        t('auth.validation_error_title') || 'Validation Error',
        errorMessages.join('\n'),
        [{ text: t('common.ok') || 'OK' }]
      );
      return;
    }

    setIsLoading(true);
    try {
      await resetPasswordApi({
        email,
        otp: values.otp,
        new_password: values.password,
      });

      Alert.alert(
        t('auth.password_reset_success_title') || 'Password Reset Successfully',
        t('auth.password_reset_success_message') || 'Your password has been reset successfully. You can now login with your new password.',
        [
          {
            text: t('common.ok') || 'OK',
            onPress: () => navigation.navigate(SCREENS.LOGIN),
          },
        ]
      );
    } catch (error: any) {
      console.error('Reset password error:', error);
      Alert.alert(
        t('auth.reset_password_failed_title') || 'Reset Password Failed',
        error.message || t('auth.reset_password_failed_message') || 'Please check your OTP and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
        <KeyboardAwareScrollView
          style={styles.keyboardContainer}
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          enableOnAndroid={true}
          enableAutomaticScroll={true}
          keyboardOpeningTime={0}
          extraScrollHeight={Platform.OS === 'ios' ? 20 : 40}
          extraHeight={Platform.OS === 'ios' ? 20 : 40}>

          {/* Hero Section */}
          <FadeIn duration={300}>
            <View style={styles.heroSection}>
              {/* Modern Gradient Background */}
              <LinearGradient
                colors={[colors.primary, '#FFB347', '#FFD700', '#FF7F50', colors.primaryLight]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.heroGradient}>
                {/* Decorative Elements */}
                <View style={styles.decorativeCircle1} />
                <View style={styles.decorativeCircle2} />
                <View style={styles.decorativeCircle3} />
              </LinearGradient>

              <TouchableOpacity
                style={styles.languageButton}
                onPress={() => setIsLanguagesModalVisible(true)}>
                <Text style={styles.languageText}>{language.toUpperCase()}</Text>
              </TouchableOpacity>

              <View style={styles.heroContent}>
                <FadeIn delay={100} duration={400}>
                  <View style={styles.logoContainer}>
                    <PyxiLabel />
                  </View>
                </FadeIn>

                <FadeIn delay={150} duration={400}>
                  <Text style={styles.taglineText}>{t('auth.reset_your_password') || 'Reset Your Password'}</Text>
                </FadeIn>
              </View>
            </View>
          </FadeIn>

          {/* Form Section */}
          <View style={styles.formSection}>
            <FadeIn delay={50} duration={300}>
              <ModernCard variant="default" padding="xl" style={{ marginBottom: spacing.lg, ...shadows.none }}>
                <Text style={styles.formTitle}>{t('auth.reset_password_title') || 'Reset Password'}</Text>
                <Text style={styles.formSubtitle}>
                  {t('auth.enter_otp_and_new_password') || 'Enter the OTP sent to your email and create a new password for'}: {email}
                </Text>

                <Formik
                  initialValues={{ otp: '', password: '', confirmPassword: '' }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {({ values, handleChange, handleSubmit, errors, touched }) => {
                    const passwordStrength = calculatePasswordStrength(values.password);
                    const hasOtpError = touched.otp && errors.otp;
                    const hasPasswordError = touched.password && errors.password;
                    const hasConfirmPasswordError = touched.confirmPassword && errors.confirmPassword;

                    return (
                      <>
                        <View style={styles.inputContainer}>
                          <ModernTextInput
                            label={t('auth.otp_code') || 'OTP Code'}
                            placeholder={t('auth.enter_otp_code') || 'Enter the OTP code'}
                            value={values.otp}
                            onChangeText={handleChange('otp')}
                            errorText={hasOtpError ? errors.otp : undefined}
                            keyboardType="numeric"
                            autoCapitalize="none"
                            autoCorrect={false}
                            maxLength={8}
                            variant="outlined"
                            size="lg"
                          />
                        </View>

                        <View style={styles.inputContainer}>
                          <ModernTextInput
                            label={t('auth.new_password') || 'New Password'}
                            placeholder={t('auth.enter_new_password') || 'Enter your new password'}
                            value={values.password}
                            onChangeText={handleChange('password')}
                            errorText={hasPasswordError ? errors.password : undefined}
                            secureTextEntry
                            autoCapitalize="none"
                            autoCorrect={false}
                            variant="outlined"
                            size="lg"
                          />

                          {values.password.length > 0 && (
                            <View style={styles.passwordStrength}>
                              <View style={styles.strengthBar}>
                                <View
                                  style={[
                                    styles.strengthFill,
                                    {
                                      width: `${(passwordStrength / 5) * 100}%`,
                                      backgroundColor: getPasswordStrengthColor(passwordStrength),
                                    },
                                  ]}
                                />
                              </View>
                              <Text
                                style={[
                                  styles.strengthText,
                                  { color: getPasswordStrengthColor(passwordStrength) },
                                ]}
                              >
                                {getPasswordStrengthText(passwordStrength)}
                              </Text>
                            </View>
                          )}
                        </View>

                        <View style={styles.inputContainer}>
                          <ModernTextInput
                            label={t('auth.confirm_new_password') || 'Confirm New Password'}
                            placeholder={t('auth.confirm_new_password_placeholder') || 'Confirm your new password'}
                            value={values.confirmPassword}
                            onChangeText={handleChange('confirmPassword')}
                            errorText={hasConfirmPasswordError ? errors.confirmPassword : undefined}
                            secureTextEntry
                            autoCapitalize="none"
                            autoCorrect={false}
                            variant="outlined"
                            size="lg"
                          />
                        </View>

                        <ModernButton
                          title={t('auth.reset_password_button') || 'Reset Password'}
                          onPress={() => {
                            Keyboard.dismiss();
                            handleSubmit();
                          }}
                          loading={isLoading}
                          disabled={isLoading}
                          variant="primary"
                          size="lg"
                          fullWidth
                          hapticFeedback
                        />

                        <View style={styles.backToLoginContainer}>
                          <Text style={styles.backToLoginText}>{t('auth.remember_password') || 'Remember your password?'}</Text>
                          <TouchableOpacity
                            style={styles.backToLoginButton}
                            onPress={() => {
                              navigation.navigate(SCREENS.LOGIN);
                            }}>
                            <Text style={styles.backToLoginButtonText}>{t('auth.back_to_login') || 'Back to Login'}</Text>
                          </TouchableOpacity>
                        </View>
                      </>
                    );
                  }}
                </Formik>
              </ModernCard>
            </FadeIn>
          </View>
        </KeyboardAwareScrollView>
      </KeyboardAvoidingView>

      <LanguagesModal
        isVisible={isLanguagesModalVisible}
        onClose={() => setIsLanguagesModalVisible(false)}
      />
    </SafeAreaView>
  );
};

export default ResetPassword;
